import React, { useEffect, useState, useRef } from 'react'
import ReactDOM from 'react-dom'
import _ from 'lodash-es'
import Stats from 'stats.js'
import { useGesture } from 'react-use-gesture'
import { countriesData, globalData } from '../data'
import { scaleByDevicePixelRatio, Align } from '../../../lib/canvas-utils'
import { useAnimationLoop } from '../../../lib/hooks/useAnimationLoop'
import { DRAW_CONSTS } from '../constants-canvas'
import { drawBars } from '../draw/draw-commons'
import { useScrollState } from '../useScrollState'
import { useInterpolatedDrawParams } from '../useInterpolatedDrawParams'
import { Header } from './Header'
import { usePositions } from '../usePositions'
import { useParentSize } from '../../../lib/hooks/useParentSize'
import { mod } from '../../../lib/math-utils'
import { Arrows } from './Arrows'
import { ChallengeLabel } from '../../../lib/ChallengeLabel'
import { ChallengesMap, CHALLENGES } from '../../../lib/constants'
import { fontsReady } from '../../../lib/fontsReady'
import { useUpdate } from '../../../lib/hooks/useUpdate'
import { ARROWS_INCREMENT } from '../constants'
import { LulucfCaption } from '../../../components/LulucfCaption'

export const StackedBar = () => {
  const canvasRef = React.useRef<HTMLCanvasElement>()
  const ctxRef = React.useRef<CanvasRenderingContext2D>()
  const [wrapperRef, { width: parentWidth }] = useParentSize<HTMLDivElement>()

  /*
    /$$$$$$  /$$$$$$$$ /$$$$$$  /$$$$$$$$ /$$$$$$$$
   /$$__  $$|__  $$__//$$__  $$|__  $$__/| $$_____/
  | $$  \__/   | $$  | $$  \ $$   | $$   | $$
  |  $$$$$$    | $$  | $$$$$$$$   | $$   | $$$$$
   \____  $$   | $$  | $$__  $$   | $$   | $$__/
   /$$  \ $$   | $$  | $$  | $$   | $$   | $$
  |  $$$$$$/   | $$  | $$  | $$   | $$   | $$$$$$$$
   \______/    |__/  |__/  |__/   |__/   |________/
  */

  const positions = usePositions(parentWidth)

  const [focusedElement, setElement] = useState<number | 'global'>('global')

  const hoveredElement = useRef({
    element: undefined as number | 'global' | undefined,
    set(target: number | 'global') {
      if (this.element === target) return
      this.element = target
      draw()
    },
    reset() {
      this.element = undefined
      draw()
    },
  }).current

  const [interpolatedDrawParams, animateDrawParams] = useInterpolatedDrawParams({
    onStart: () => startLoop(),
    onStop: () => stopLoop(),
  })

  // WHEN A NEW COUNTRY IS FOCUSED
  // TRIGGER COUNTRY AND LABEL ANIMATION
  useEffect(() => {
    animateDrawParams(focusedElement)
  }, [focusedElement, animateDrawParams])

  const [scroll, scrollTo, stopScroll] = useScrollState({
    onStart: () => startLoop(),
    onComplete: () => stopLoop(),
    to: (state, target: number | 'global') =>
      positions.getScrollToFocusTarget(target, state.total, Math.sign(state.speed) as 0 | 1 | -1),
  })

  function scrollAndSetElement(el: number | 'global') {
    setElement(el)
    scrollTo(el)
  }

  const incrementAndScrollBy = (elementsAmount: number) => {
    const currentIndex = focusedElement === 'global' ? countriesData.length : focusedElement
    const nextIndex = mod(currentIndex + elementsAmount, countriesData.length + 1)
    const nextElement = nextIndex === countriesData.length ? 'global' : nextIndex

    scrollAndSetElement(nextElement)
  }

  /*
    /$$$$$$  /$$$$$$$$  /$$$$$$  /$$$$$$$$ /$$   /$$ /$$$$$$$  /$$$$$$$$
   /$$__  $$| $$_____/ /$$__  $$|__  $$__/| $$  | $$| $$__  $$| $$_____/
  | $$  \__/| $$      | $$  \__/   | $$   | $$  | $$| $$  \ $$| $$
  | $$ /$$$$| $$$$$   |  $$$$$$    | $$   | $$  | $$| $$$$$$$/| $$$$$
  | $$|_  $$| $$__/    \____  $$   | $$   | $$  | $$| $$__  $$| $$__/
  | $$  \ $$| $$       /$$  \ $$   | $$   | $$  | $$| $$  \ $$| $$
  |  $$$$$$/| $$$$$$$$|  $$$$$$/   | $$   |  $$$$$$/| $$  | $$| $$$$$$$$
   \______/ |________/ \______/    |__/    \______/ |__/  |__/|________/
  */

  // HANDLER LISTENER FOR DRAGGING
  // When user taps, following events are fired:
  // - touchstart
  // - touchend
  // - mousemove
  // - mousedown
  // - click
  //
  // When user drags with touch, following events are fired:
  // - touchstart
  // - touchmove
  // - touchend
  //
  // To avoid highlighting country on tap:
  // - on touchstart: set `isTouch = true`
  // - on mousemove: if touch is true, set `isTouch = false` and skip, else highlight
  const bind = useGesture(
    {
      onTouchStart: () => scroll.updateOnTouchStart(),
      onClick: (e) => {
        const { left, top } = canvasRef.current.getBoundingClientRect()
        const x = e.clientX - left
        const y = e.clientY - top
        hoveredElement.reset()

        if (scroll.hasDragged) {
          scroll.resetDragCount()
          return
        }

        for (const label of Object.values(labels)) {
          if (!label) continue
          const isClicked = label.labelOptions.opacity && label.isInside({ x, y })

          if (isClicked) {
            Object.values(labels).forEach((label) => label.toggleShowChallenge())
            return
          }
        }

        const pointedCountry = positions.getElementFromPos(x, scroll.total)
        scrollAndSetElement(pointedCountry)
      },

      onMouseMove: (e) => {
        if (scroll.isTouch) {
          scroll.isTouch = false
          return
        }

        if (scroll.isDragging) return
        const { left, top } = canvasRef.current.getBoundingClientRect()
        const x = e.clientX - left
        const y = e.clientY - top

        if (Object.values(labels).some((l) => l.isInside({ x, y }))) {
          hoveredElement.reset()
          canvasRef.current.style.cursor = 'pointer'
          return
        }

        canvasRef.current.style.cursor = 'grab'

        const hovered = positions.getElementFromPos(x, scroll.total)
        hoveredElement.set(hovered)
      },
      onMouseOut: () => hoveredElement.reset(),
      onDragStart: () => {
        document.body.style.cursor = 'grabbing'
        canvasRef.current.style.cursor = 'inherit'

        startLoop()
        stopScroll()
        scroll.updateOnDragStart()
        hoveredElement.reset()
      },

      onDrag: (state): void => {
        if (!scroll.isDragging) return
        state.event.preventDefault()
        scroll.updateOnDrag(state)
        const country = positions.getFocusedElement(scroll.total)
        setElement(country)
      },
      onDragEnd: () => {
        document.body.style.cursor = 'default'
        canvasRef.current.style.cursor = 'grab'

        if (scroll.hasDragged) {
          const pointedCountry = positions.getFocusedElement(scroll.total, scroll.speed)
          scrollAndSetElement(pointedCountry)
        }

        stopLoop()
        scroll.updateOnDragEnd()
      },
    },
    {
      domTarget: canvasRef,
      eventOptions: { passive: false },
      drag: { axis: 'x' },
    }
  )

  useEffect(bind, [bind])

  /*
   /$$$$$$$  /$$$$$$$   /$$$$$$  /$$      /$$
  | $$__  $$| $$__  $$ /$$__  $$| $$  /$ | $$
  | $$  \ $$| $$  \ $$| $$  \ $$| $$ /$$$| $$
  | $$  | $$| $$$$$$$/| $$$$$$$$| $$/$$ $$ $$
  | $$  | $$| $$__  $$| $$__  $$| $$$$_  $$$$
  | $$  | $$| $$  \ $$| $$  | $$| $$$/ \  $$$
  | $$$$$$$/| $$  | $$| $$  | $$| $$/   \  $$
  |_______/ |__/  |__/|__/  |__/|__/     \__/
  */

  const labels = useRef<ChallengesMap<ChallengeLabel>>(
    _.zipObject(CHALLENGES, Array(CHALLENGES.length).fill(undefined)) as any
  ).current

  const hideLabels = () => {
    Object.values(labels).forEach((label) => label.setShowChallenge(false))
  }

  useUpdate(hideLabels, [focusedElement])

  function setup() {
    ctxRef.current = canvasRef.current?.getContext('2d')
    scaleByDevicePixelRatio(
      canvasRef.current,
      ctxRef.current,
      positions.canvasWidth,
      DRAW_CONSTS.HEIGHT
    )

    if (labels.agriculture) return

    const opts = {
      showChallenge: true,
      showPercentage: true,
      startLoop: () => startLoop(),
      stopLoop: () => stopLoop(),
      align: 'top right' as Align,
    }

    CHALLENGES.forEach((challenge) => {
      labels[challenge] = new ChallengeLabel(ctxRef.current, {
        ...opts,
        challenge,
        percentage: interpolatedDrawParams.country[challenge] * 100,
        opacity: interpolatedDrawParams.labelsOpacity[challenge],
        y: interpolatedDrawParams.labelsPosition[challenge],
        x: positions.labelsRightPos,
        postfix: challenge === 'agriculture' && interpolatedDrawParams.hasLULUCF ? '*' : '',
      })
    })
  }

  const [draw, startLoop, stopLoop] = useAnimationLoop(() => {
    const ctx = ctxRef.current
    if (!ctx) return
    ctx.clearRect(0, 0, positions.canvasWidth, DRAW_CONSTS.HEIGHT)

    // DRAW ALL COUNTRIES
    countriesData.forEach((country, i) => {
      const x = positions.getCountryXPos(scroll.total, i)

      ctx.globalAlpha =
        i === hoveredElement.element
          ? DRAW_CONSTS.HOVERED_OPACITY
          : positions.getOpacityFromPos(x + DRAW_CONSTS.COUNTRY_WIDTH / 2)
      drawBars({
        ctx,
        data: country,
        x,
        width: DRAW_CONSTS.COUNTRY_WIDTH,
        height: DRAW_CONSTS.HEIGHT,
      })
    })

    // DRAW GLOBAL
    const globalX = positions.getGlobalXPos(scroll.total)

    ctx.globalAlpha =
      hoveredElement.element === 'global'
        ? DRAW_CONSTS.HOVERED_OPACITY
        : positions.getOpacityFromPos(globalX + DRAW_CONSTS.GLOBAL_WIDTH / 2)
    drawBars({
      ctx,
      data: globalData,
      x: globalX,
      width: DRAW_CONSTS.GLOBAL_WIDTH,
      height: DRAW_CONSTS.HEIGHT,
    })

    // DRAW FOCUSED COUNTRY
    ctx.globalAlpha = 1
    drawBars({
      ctx,
      data: interpolatedDrawParams.country,
      x: positions.canvasWidth / 2 - interpolatedDrawParams.width / 2,
      width: interpolatedDrawParams.width,
      height: DRAW_CONSTS.HEIGHT,
    })

    Object.entries(labels).map(([challenge, label]) => {
      if (!label) return

      label.update({
        opacity: interpolatedDrawParams.labelsOpacity[challenge],
        y: interpolatedDrawParams.labelsPosition[challenge],
        percentage: interpolatedDrawParams.country[challenge] * 100,
        x: positions.labelsRightPos,
        postfix: challenge === 'agriculture' && interpolatedDrawParams.hasLULUCF ? '*' : '',
      })

      label.draw()
    })
  }, [])

  useEffect(() => {
    setup()
    draw()
  }, [parentWidth])

  useEffect(() => {
    fontsReady().then(() => {
      draw()
    })
  }, [])

  /*
   /$$$$$$$  /$$$$$$$$ /$$   /$$ /$$$$$$$  /$$$$$$$$ /$$$$$$$
  | $$__  $$| $$_____/| $$$ | $$| $$__  $$| $$_____/| $$__  $$
  | $$  \ $$| $$      | $$$$| $$| $$  \ $$| $$      | $$  \ $$
  | $$$$$$$/| $$$$$   | $$ $$ $$| $$  | $$| $$$$$   | $$$$$$$/
  | $$__  $$| $$__/   | $$  $$$$| $$  | $$| $$__/   | $$__  $$
  | $$  \ $$| $$      | $$\  $$$| $$  | $$| $$      | $$  \ $$
  | $$  | $$| $$$$$$$$| $$ \  $$| $$$$$$$/| $$$$$$$$| $$  | $$
  |__/  |__/|________/|__/  \__/|_______/ |________/|__/  |__/
  */

  const lulucf = (focusedElement !== 'global' ? countriesData[focusedElement] : globalData)
    .lulucf_emission_mmt_co2

  return (
    <div className="-stacked-bar-container remove-prod-margins">
      <Header
        selected={countriesData[focusedElement] || { country: 'Global', country_code: 'global' }}
        onChange={(selected) =>
          scrollAndSetElement(
            selected.country_code === 'global'
              ? 'global'
              : countriesData.findIndex((c) => c.country_code === selected.country_code)
          )
        }
      />
      <div className="relative user-select-none" ref={wrapperRef}>
        <canvas ref={canvasRef} style={{ cursor: 'grab' }} />

        <Arrows
          onPrev={() => incrementAndScrollBy(-ARROWS_INCREMENT)}
          onNext={() => incrementAndScrollBy(ARROWS_INCREMENT)}
        />
      </div>
      <div className="h3 fw2 flex justify-center">
        {lulucf !== 0 && <LulucfCaption classNames="ma2 tc" emissions={lulucf} />}
      </div>
    </div>
  )
}

/*
  /$$      /$$  /$$$$$$  /$$   /$$ /$$   /$$ /$$$$$$$$
 | $$$    /$$$ /$$__  $$| $$  | $$| $$$ | $$|__  $$__/
 | $$$$  /$$$$| $$  \ $$| $$  | $$| $$$$| $$   | $$
 | $$ $$/$$ $$| $$  | $$| $$  | $$| $$ $$ $$   | $$
 | $$  $$$| $$| $$  | $$| $$  | $$| $$  $$$$   | $$
 | $$\  $ | $$| $$  | $$| $$  | $$| $$\  $$$   | $$
 | $$ \/  | $$|  $$$$$$/|  $$$$$$/| $$ \  $$   | $$
 |__/     |__/ \______/  \______/ |__/  \__/   |__/
 */

export function mountStackedBar(node: HTMLElement) {
  ReactDOM.render(
    <div className="center" style={{ maxWidth: 1312 }}>
      <StackedBar />
    </div>,
    node
  )
}
